/**
 * How a person's category presents itself.
 *
 * Out of `components/ui/status-colors.ts:52-80`, three of that file's seven ways, plus
 * `app/app/people/utils/people-constants.ts`, which is deleted rather than moved.
 *
 * **Only one of that file's two declarations was a duplicate.** It re-declared `PersonCategory`
 * from the same Supabase enum this module names — the third of at least four such aliases in the
 * tree — but `CATEGORY_FILTERS` was its own, and its labels are *plural* ("Friends") where
 * `PERSON_CATEGORY_LABELS` is singular ("Friend"). Deriving one from the other would change what
 * the filter row reads, so both survive.
 */

import type { Database } from '@/account'

export type PersonCategory = Database['public']['Enums']['person_category']

/**
 * Person category badge color classes
 */
export const PERSON_CATEGORY_COLORS: Record<PersonCategory, string> = {
  family: 'bg-rose-500/10 text-rose-600 border-rose-500/20 dark:text-rose-400',
  friend:
    'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400',
  work: 'bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400',
}

/**
 * Person category labels for display
 */
export const PERSON_CATEGORY_LABELS: Record<PersonCategory, string> = {
  family: 'Family',
  friend: 'Friend',
  work: 'Work',
}

/**
 * Person category avatar background colors
 */
export const PERSON_AVATAR_COLORS: Record<PersonCategory, string> = {
  family: 'bg-rose-500/15',
  friend: 'bg-emerald-500/15',
  work: 'bg-blue-500/15',
}

/** The category filter row above the people list. Plural labels, and an "All" that is not a category. */
export const CATEGORY_FILTERS: {
  value: PersonCategory | 'all'
  label: string
}[] = [
  { value: 'all', label: 'All' },
  { value: 'family', label: 'Family' },
  { value: 'friend', label: 'Friends' },
  { value: 'work', label: 'Work' },
]
